import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setToast } from "../redux/slice/system";
import { useAbortedEffect } from "../components/hooks";
import { CommonAPI } from "../axios/api";

import { formatGroupToLevelCount } from "../service/fromatApiRes";
import ModuleProgressLoading from "./progressLoading";
import { system_api } from "../service/system";

export default function ModuleChartSingleView({ systemWeb, moduleData }) {
  const dispatch = useDispatch();
  const redux_diagram = useSelector((state) => state.diagram);
  const [filterData, setFilterData] = useState({
    filterSetting: -1,
    dateRangeSetting: -1,
  });
  const [reload, setReload] = useState(true);
  const [categoryData, setCategoryData] = useState(null);
  const [apiData, setApiData] = useState(null);
  const [totalRecord, setTotalRecord] = useState(0);
  const [headColumns, setHeadColumns] = useState(null);
  const [levelColumns, setLevelColumns] = useState(null);
  const [exportData, setExportData] = useState(null);
  const [isResetPage, setIsResetPage] = useState(false);
  const [queryPending, setQueryPending] = useState(false);
  const [exportPending, setExportPending] = useState(false);
  const queryControllerRef = useRef(null);
  const exportControllerRef = useRef(null);
  const queryPendingRef = useRef(false);
  const exportPendingRef = useRef(false);
  const queryKeyRef = useRef(null);
  const latestQueryIdRef = useRef(0);

  useEffect(
    () => () => {
      queryControllerRef.current?.abort();
      exportControllerRef.current?.abort();
    },
    []
  );

  useAbortedEffect(
    (signal) => {
      let filterKey = moduleData
        ? moduleData.filterMainKey ?? moduleData.key
        : null;

      if (
        !filterKey ||
        (redux_diagram.filterSetting[filterKey] === filterData.filterSetting &&
          redux_diagram.dateRangeSetting[filterKey] ===
            filterData.dateRangeSetting)
      )
        return;

      setFilterData({
        filterSetting: redux_diagram.filterSetting[filterKey],
        dateRangeSetting: redux_diagram.dateRangeSetting[filterKey],
      });

      callMainAPI({ signal, filterKey, page: 1 });
    },
    [moduleData, redux_diagram]
  );

  const callMainAPI = ({
    signal,
    filterKey,
    isExport = false,
    page = 1,
  } = {}) => {
    if (!moduleData?.api || !systemWeb) {
      return Promise.resolve({
        Response: -1,
        message: "圖表設定不完整",
      });
    }
    filterKey = filterKey ?? moduleData.filterMainKey ?? moduleData.key;

    let isFormatSelfArr = [
      "Diabetes/QuestionnaireYearAndMonthClosedCount",
      "HealthCenterHealthCheckup/QuestionnaireYearAndMonthClosedCount",
      "ServiceApplication/AcceptCaseStatistic",
      "ElderlyHealth/IADLTotalScoreToCheckYear",
    ];
    let params = {
      isRaw: isExport ? true : moduleData.isRaw || false,
      isPage: moduleData.isPage,
      filter: moduleData.filter || null,
      dateRangeColumns: redux_diagram.dateRangeSetting[filterKey] ?? null,
      groupOrders: moduleData.groupOrders || {},
      groupColumns: moduleData.groupColumns || [],
      headColumns: moduleData.headColumns || [],
      levelColumns: moduleData.levelColumns || [],
      categoryColumns: [
        ...(moduleData.categoryColumns || []),
        ...(moduleData.filterColumns || []),
      ],
      filterColumns: redux_diagram.filterSetting[filterKey]
        ? Object.entries(redux_diagram.filterSetting[filterKey])
            .filter(([key]) => key.indexOf("noback") === -1)
            .reduce((pre, cur) => ({ ...pre, [cur[0]]: cur[1] }), {})
        : null,
      countKey: moduleData.countKey,
    };

    // 分頁設定
    if (moduleData.isPage && !isExport) {
      const normalizedPage = Number(page);
      params = {
        ...params,
        page:
          isFormatSelfArr.includes(moduleData.path) || moduleData.page === null
            ? null
            : Number.isInteger(normalizedPage) && normalizedPage > 0
            ? normalizedPage
            : 1,
        size: 10,
      };
    }

    // test
    // params = {
    //   ...params,
    //   isRaw: true,
    //   isPage: true,
    //   page: null,
    // };

    const requestKey = JSON.stringify({
      path: `${systemWeb}/${moduleData.api}`,
      params,
    });

    if (isExport) {
      if (exportPendingRef.current) {
        return Promise.resolve({
          Response: -2,
          duplicate: true,
          message: "",
        });
      }
      exportPendingRef.current = true;
      setExportPending(true);
    } else {
      if (
        queryPendingRef.current &&
        queryKeyRef.current === requestKey
      ) {
        return Promise.resolve({
          Response: -2,
          duplicate: true,
          message: "",
        });
      }

      queryControllerRef.current?.abort();
      queryPendingRef.current = true;
      queryKeyRef.current = requestKey;
      setQueryPending(true);
      setReload(true);
    }

    const controller = new AbortController();
    const controllerRef = isExport
      ? exportControllerRef
      : queryControllerRef;
    controllerRef.current = controller;

    if (signal) {
      if (signal.aborted) controller.abort();
      else
        signal.addEventListener("abort", () => controller.abort(), {
          once: true,
        });
    }

    const queryId = isExport ? null : ++latestQueryIdRef.current;
    const isLatestQuery = () =>
      isExport || queryId === latestQueryIdRef.current;

    return CommonAPI.postRoot({
      options: {
        signal: controller.signal,
        timeout: isExport
          ? system_api.exportTimeout
          : system_api.queryTimeout,
      },
      path: `${systemWeb === "HEALTH_A" ? "HEALTHCARE_CHIAYI" : systemWeb}/${
        moduleData.api
      }`,
      data: params,
    }).then((result) => {
      let { Response, data, message } = result;

      if (Response === 1) {
        if (!isLatestQuery()) return result;

        if (isFormatSelfArr.includes(moduleData.path)) {
          if (moduleData.formatRows) {
            data.data = data.data.map((item) =>
              moduleData.formatRows({ data: item })
            );
          }

          data = formatGroupToLevelCount({
            ...params,
            createColumns: moduleData.createColumns,
            data: data.data,
            isRaw: false,
            isPage: false,
          });
        }

        setHeadColumns(data.columns);
        setLevelColumns(data.levelColumns);

        if (moduleData.isPage) setIsResetPage(params.page === 1);

        if (moduleData.groupColumns?.[0] && !moduleData.groupOrders) {
          let key = moduleData.groupColumns?.[0].key;
          data.data.sort((pre, next) => {
            if (typeof pre[key] === "number" && typeof next[key] === "number")
              return pre[key] - next[key];
            else if (pre[key] && next[key])
              return pre[key].localeCompare(next[key]);
            else return 0;
          });
        }

        if (isExport) setExportData(data.data);
        else {
          setApiData(data.data);
          setTotalRecord(data.total ?? 0);
          if (!params.filterColumns) setCategoryData(data.category);
        }
      } else if (!result.canceled && !result.duplicate && isLatestQuery()) {
        if (!isExport) setReload(false);
        dispatch(
          setToast({
            severity: "error",
            summary: message,
            detail: "",
          })
        );
      }

      return result;
    }).finally(() => {
      if (controllerRef.current !== controller) return;

      controllerRef.current = null;

      if (isExport) {
        exportPendingRef.current = false;
        setExportPending(false);
        return;
      }

      queryPendingRef.current = false;
      queryKeyRef.current = null;
      setQueryPending(false);

      if (
        ![
          "ElderlyHealthStatisticsReport",
          "ElderlyHealthStatisticsReport112",
          "ElderlyHealthStatisticsReport113",
          "ElderlyHealthVisitMonthly",
          "ElderlyHealthVisitMonthly113",
        ].includes(moduleData.key)
      )
        setReload(false);
    });
  };

  callMainAPI.queryPending = queryPending;
  callMainAPI.exportPending = exportPending;

  return (
    <>
      <div className="relative border-1 border-400 overflow-hidden">
        {reload && <ModuleProgressLoading />}

        <div className="-m-1px">
          {moduleData?.render({
            moduleTitle: moduleData.label,
            moduleData: moduleData
              ? {
                  ...moduleData,
                  levelColumns,
                  columns:
                    moduleData.columns.length > 0
                      ? moduleData.columns
                      : headColumns,
                  // columns:
                  //   !categoryData ||
                  //   Object.keys(categoryData).length === 0 ||
                  //   !moduleData.categoryColumns
                  //     ? moduleData.columns
                  //     : formatLevelColumns({ data: levelColumns }) || [],
                  categoryData,
                }
              : {},
            apiData,
            exportData,
            totalRecord,
            callMainAPI,
            isResetPage,
            setReload,
          })}
        </div>
      </div>
    </>
  );
}
